import { useToast } from '@/components/ui/use-toast';

interface ConflictResolutionResult {
  preserved: number;
  accepted: number;
  needsManualReview: number;
  details: Array<{
    patientId: string;
    measureId: string;
    decision: boolean | 'conflict';
    reason: string;
    description: string;
  }>;
}

/** Always summary toasts — notification level is not user-configurable. */
export function useConflictNotifications() {
  const { toast } = useToast();

  const showConflictResolution = (result: ConflictResolutionResult) => {
    const totalConflicts = result.preserved + result.accepted;

    if (totalConflicts > 0) {
      toast({
        title: 'Conflict Resolution Complete',
        description: `Processed ${totalConflicts} conflicts: ${result.preserved} preserved, ${result.accepted} accepted.`,
        duration: 5000,
      });
    }

    if (result.needsManualReview > 0) {
      toast({
        title: 'Manual Review Required',
        description: `${result.needsManualReview} conflicts require your decision. Check the Conflicts dashboard.`,
        variant: 'default',
        duration: 10000,
      });
    }
  };

  return { showConflictResolution };
}

/** Hook for components that need to trigger conflict resolution notifications */
export function useConflictResolutionFeedback() {
  const notifications = useConflictNotifications();

  const handleOutcomeUpdateResponse = (response: any) => {
    if (response.conflictResolution) {
      const result: ConflictResolutionResult = {
        preserved: response.conflictResolution.decision === false ? 1 : 0,
        accepted: response.conflictResolution.decision === true ? 1 : 0,
        needsManualReview: response.conflictResolution.decision === 'conflict' ? 1 : 0,
        details: [{
          patientId: response.patientId || 'Unknown',
          measureId: response.measureId || 'Unknown',
          decision: response.conflictResolution.decision,
          reason: response.conflictResolution.reason,
          description: response.conflictResolution.description,
        }],
      };

      notifications.showConflictResolution(result);
    }
  };

  return {
    handleOutcomeUpdateResponse,
    ...notifications,
  };
}
